import { useContext, useEffect, useState } from "react"
import styled from "styled-components"
import { ConversationContext } from "../contexts/ConversationContext"
import { useLocalStorage } from "usehooks-ts"
import { backendProxy } from "../hooks/backendProxy";

const FeedbackRequestScreenWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
`

const FeedbackScreenBackground = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: #000;
    opacity: 0.5;
`
const FeedbackScreenContent = styled.div`
    position: fixed;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -moz-transform: translate(-50%, -50%);
    -o-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    z-index: 999;
    width: 600px;
    background-color: #fff;
    color: #000;
    padding: 5px 15px 15px 15px;
    border: 1px solid #ddd;
    border-radius: 5px;
    transition: opacity 0.3s;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
    @media (max-width: 768px) {width: 80%;}
    h1 {
        font-family: Helvetica, Arial, sans-serif;
        font-weight: 600;
        font-size: 26px;
        line-height: 31.2px;
        @media (max-width: 600px) {font-size: 20px; line-height: 24px;}
    }
    p {
        font-family: Helvetica, Arial, sans-serif;
        font-weight: 200;
        font-size: 16px;
        line-height: 19.2px;
    }
    .btn {
        border: 2px solid transparent;
        white-space: normal;
        font-weight: 600; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
        font-size: 16px;
        text-align: left;
        min-height: 42px;
        padding: 3px 15px;
        margin-right: 10px;
        display: -webkit-inline-flex;
        justify-content: center;
        align-items: center;
        -webkit-justify-content: center;
        -webkit-align-items: center;
        display: -moz-inline-flex;
        -moz-justify-content: center;
        -moz-align-items: center;
        @media (max-width: 768px) {margin-bottom: 10px;}
        @media (max-width: 480px) {width: 100%;}
        &:hover {cursor: pointer;}
        &.btn-primary {
            box-shadow: none; -webkit-box-shadow: none; text-shadow: none;
            background-color: #008334;
            border-color: #008334;
            color: #fff;
            &:hover {
                background-color: #008334;
                border-color: #008334;
                color: #fff;
                text-decoration: none !important;
            }
        }
        &.btn-secondary {
            background-color: #fff;
            border-color: #cfcfcf;
            color: #008334;
            &:hover {
                border-color: #008334;
                text-decoration: none !important;
            }
        }
    }
`
const oneDay = 24 * 60 * 60 * 1000;
const validityDurationInDays = 1000; // means: disabled for now
const minimumNofMessages = 3; 
const waitingTimeBeforeShowForm = 7500; //ms

/*
TODO: Definition Anzahl Messages, bis diese Meldung angezeigt wird und wann diese ablaufen soll.
*/
export const FeedbackRequestScreen = () => {
    const {messages, conversationState, clientId} = useContext(ConversationContext);
    const [feedbackRequestShown, setFeedbackRequestShown] = useLocalStorage("feedback-request-shown", "")
    const [formVisible, setFormVisible] = useState<Date>();

    const neverShown = !feedbackRequestShown;
    const validityDuration = validityDurationInDays * oneDay;
    const expired = feedbackRequestShown && (new Date().getTime() - parseInt(feedbackRequestShown)) > validityDuration;
    const type = expired ? "expired": "never";

    useEffect(() => {
        setFormVisible(undefined);
    }, [feedbackRequestShown]);

    useEffect(() => {
        if (conversationState === 'idle' && messages.length >= 2*minimumNofMessages && (expired || neverShown)) {
            setTimeout(() => {
                setFormVisible(new Date());
            }, waitingTimeBeforeShowForm);
        }
    }, [conversationState, messages, expired, neverShown])

    const close = (closingDate: Date) => {
        setFeedbackRequestShown("" + closingDate.getTime())
    }
    const onAcceptNow = async () => {
        const timeToDecision = new Date().getTime() - (formVisible || new Date()).getTime();
        await backendProxy.trackEvent('feedback-request-accept-now', clientId, { type, timeToDecision });
        close(new Date());
        //window.location.href = `https://www.sg.ch/tools/ki-suche/feedback.html#type=${type}&source=${encodeURIComponent(window.location.href)}`;
        window.open(`https://www.sg.ch/tools/ki-suche/feedback.html`, '_blank');
    }
    const onAcceptTomorrow = async () => {
        const timeToDecision = new Date().getTime() - (formVisible || new Date()).getTime();
        await backendProxy.trackEvent('feedback-request-accept-tomorrow', clientId, { type, timeToDecision });
        close(new Date((new Date().getTime() - (validityDurationInDays - 1) * oneDay)));
    }
    const onCancel = async () => {
        const timeToDecision = new Date().getTime() - (formVisible || new Date()).getTime();
        await backendProxy.trackEvent('feedback-request-cancel', clientId, { type, timeToDecision });
        close(new Date());
    }
    if (!formVisible) {
        return null;
    }
    return (
        <FeedbackRequestScreenWrapper>
            <FeedbackScreenBackground />
            <FeedbackScreenContent>
                <>
                    <h1>Uns interessiert deine Meinung!</h1>
                    <p>
                        { neverShown && <span>Nachdem nun deine ersten Anfragen beantwortet wurden interessiert uns dein genereller Eindruck. </span> }
                        { expired && <span>Es ist schon eine Weile her, seit wir dich das letzte Mal um deine Meinung gefragt haben.</span>}
                        <br/>
                        Es würde uns freuen, wenn du uns ein paar Fragen beantworten würdest. Es dauert nur 1-2 Minuten - versprochen!
                    </p>
                    <div>
                        <button className="btn btn-primary" onClick={onAcceptNow}>An Umfrage jetzt teilnehmen</button>
                        <button className="btn btn-secondary" onClick={onAcceptTomorrow}>Vielleicht später</button>
                        <button className="btn btn-secondary" onClick={onCancel}>Kein Interesse</button>
                    </div>
                </>
            </FeedbackScreenContent>
        </FeedbackRequestScreenWrapper>
    );
}